import { notification } from "antd";

type ErroApresentavel = {
  titulo: string;
  detalhes: string[];
  statusCode: number;
};

const MENSAGEM_SEM_RESPOSTA =
  "Não foi possível se conectar ao serviço. Verifique sua conexão e tente novamente.";

function mensagemPorStatus(status: number) {
  switch (status) {
    case 400:
      return "Requisição inconsistente, faltam dados. Contate o suporte!";
    case 401:
      return "Sessão expirada ou credenciais inválidas. Faça login novamente.";
    case 403:
      return "Usuário sem permissões suficientes para isso!";
    case 404:
      return "Recurso não encontrado!";
    case 409:
      return "Conflito. Contate o suporte!";
    case 418:
      return "Servidor se recusou a coar café num bule de chá!";
    case 500:
      return "Erro interno, contate o suporte.";
    default:
      return MENSAGEM_SEM_RESPOSTA;
  }
}

/**
 * Requisições com `byteArrayResponse` (relatórios/PDF) usam
 * responseType: "arraybuffer" — o corpo de erro também chega binário e
 * precisa ser decodificado antes de qualquer leitura.
 */
const ehBinario = (data: any) =>
  // toString em vez de instanceof: o ArrayBuffer pode vir de outro realm.
  Object.prototype.toString.call(data) === "[object ArrayBuffer]" ||
  ArrayBuffer.isView(data);

function normalizarCorpo(data: any): any {
  if (ehBinario(data)) {
    if (typeof TextDecoder === "undefined") return undefined;
    try {
      const texto = new TextDecoder("utf-8").decode(data as ArrayBuffer);
      try {
        return JSON.parse(texto);
      } catch {
        return texto;
      }
    } catch {
      return undefined;
    }
  }
  return data;
}

function paraTexto(valor: any): string[] {
  if (valor === null || valor === undefined) return [];
  if (typeof valor === "string") {
    const texto = valor.trim();
    return texto ? [texto] : [];
  }
  if (Array.isArray(valor)) {
    return valor.flatMap(paraTexto);
  }
  if (typeof valor === "object") {
    // ProblemDetails de validação do ASP.NET: { "Campo": ["msg1", "msg2"] }
    return Object.entries(valor).flatMap(([chave, item]) => {
      const mensagens = paraTexto(item);
      return mensagens.map((mensagem) =>
        chave && !/^\d+$/.test(chave) && !mensagem.includes(chave)
          ? `${chave}: ${mensagem}`
          : mensagem
      );
    });
  }
  return [String(valor)];
}

/**
 * Extrai título + detalhes de qualquer formato de erro que a API devolva:
 * - envelope próprio `{ message, errors[] }` (ExceptionHandlingMiddleware);
 * - string crua (controllers com `BadRequest(ex.Message)`);
 * - ProblemDetails do ASP.NET (`title`/`detail`/`errors`);
 * - corpo vazio (cai na mensagem padrão do status).
 */
export function extrairErro(error: any): ErroApresentavel {
  if (!error?.response) {
    const semResposta =
      error?.code === "ECONNABORTED"
        ? "Tempo de resposta esgotado. Tente novamente."
        : MENSAGEM_SEM_RESPOSTA;
    return { titulo: semResposta, detalhes: [], statusCode: 0 };
  }

  const statusCode = error.response.status ?? 0;
  const data = normalizarCorpo(error.response.data);

  if (typeof data === "string" && data.trim()) {
    return { titulo: data.trim(), detalhes: [], statusCode };
  }

  const titulo =
    (typeof data?.message === "string" && data.message.trim()) ||
    (typeof data?.Message === "string" && data.Message.trim()) ||
    (typeof data?.detail === "string" && data.detail.trim()) ||
    (typeof data?.title === "string" && data.title.trim()) ||
    "";

  const detalhes = Array.from(
    new Set(paraTexto(data?.errors ?? data?.Errors))
  ).filter((detalhe) => detalhe !== titulo); // evita repetir o título na lista

  if (!titulo && detalhes.length > 0) {
    return { titulo: detalhes[0], detalhes: detalhes.slice(1), statusCode };
  }

  return {
    titulo: titulo || mensagemPorStatus(statusCode),
    detalhes,
    statusCode,
  };
}

const montarDescricao = (detalhes: string[]) =>
  detalhes.length > 0 ? detalhes.map((item) => `• ${item}`).join("\n") : undefined;

/**
 * Exibe o erro ao usuário.
 *
 * Usa `notification` do antd (portal próprio, não depende de nenhum container
 * montado na árvore). A implementação anterior usava react-toastify sem que
 * `<ToastContainer/>` estivesse montado em lugar nenhum da aplicação, então
 * nenhum erro chegava à tela: o usuário via o Salvar da matrícula simplesmente
 * não fazer nada.
 */
export function errorHandler(error: any) {
  const { titulo, detalhes, statusCode } = extrairErro(error);

  const config = {
    message: titulo,
    description: montarDescricao(detalhes),
    placement: "topRight" as const,
    duration: detalhes.length > 0 ? 12 : 8,
    style: { whiteSpace: "pre-line" as const },
  };

  if (statusCode >= 400 && statusCode < 500) {
    notification.warning(config);
    return;
  }

  notification.error(config);
}

/** Exibe um erro próprio da tela no mesmo formato dos erros vindos da API. */
export function mostrarErro(titulo: string, detalhes: string[] = []) {
  notification.error({
    message: titulo,
    description: montarDescricao(detalhes),
    placement: "topRight",
    duration: 12,
    style: { whiteSpace: "pre-line" },
  });
}
